import { useCallback, useState, useMemo, useEffect, useRef } from 'react';

import { getLimits } from 'lib/services/limits';
import { getImplementation } from 'lib/services/implementations';
import { ENTITIES } from 'lib/constants';

const LIMIT_ENTITIES_NAMES = {
    [ENTITIES.ACCOUNTS.importExport]: 'lim_max_accounts',
    [ENTITIES.CONTACTS.importExport]: 'lim_max_contacts',
    [ENTITIES.OPPORTUNITIES.importExport]: 'lim_max_opportunities',
};

const useAvoidDuplicates = ({ entityName, data, licenseEditionCode }) => {
    const isUsers = entityName === 'users';
    const [modalConfirmIsOpen, setModalConfirmIsOpen] = useState(false);
    const [modalLimitsIsOpen, setModalLimitsIsOpen] = useState(false);
    const [duplicatesForm, setDuplicatesForm] = useState({
        enabled: isUsers,
        type: 0,
    });
    const [limits, setLimits] = useState(null);
    const [licenseCode, setLicenseCode] = useState(licenseEditionCode || '');
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender?.current) {
            isFirstRender.current = false;

            const implementationPromise = licenseCode ? Promise.resolve() : getImplementation();

            Promise.all([getLimits(), implementationPromise]).then(
                ([responseLimits, implementation]) => {
                    const data = responseLimits.data || [];
                    const newLimits = data.reduce((obj, current) => {
                        if (current?.itemCode === LIMIT_ENTITIES_NAMES[entityName]) {
                            obj = {
                                limitUsed: current?.limitUsed || 0,
                                limit: current?.value || 0,
                            };
                        }
                        return obj;
                    }, {});
                    setLimits(newLimits);

                    if (licenseCode || !implementation?.data?.licenseEditionCode) return;

                    setLicenseCode(implementation?.data?.licenseEditionCode);
                },
            );
        }
    }, [entityName, licenseCode]);

    const isOverLimits = useMemo(() => {
        const newRows = data.rows || 0;
        if (!newRows || !limits?.limit) return false;
        const existingRows = limits?.limitUsed || 0;
        const totalRows = newRows + existingRows;
        return totalRows > limits.limit;
    }, [data?.rows, limits]);

    const shouldConsiderLimits = useMemo(() => {
        if (data.importType !== 'create' || !['starter', 'essential'].includes(licenseCode))
            return false;
        return true;
    }, [licenseCode, data.importType]);

    const onConfirmLimits = useCallback(() => {
        setModalLimitsIsOpen(false);
        setModalConfirmIsOpen(true);
    }, []);

    const onCancelLimits = useCallback(() => {
        setModalLimitsIsOpen(false);
    }, []);

    const onConfirm = useCallback(() => {
        if (isOverLimits && shouldConsiderLimits) {
            setModalLimitsIsOpen(true);
            return;
        }
        setModalConfirmIsOpen(true);
    }, [isOverLimits, shouldConsiderLimits]);

    const onCancelConfirm = useCallback(() => {
        setModalConfirmIsOpen(false);
    }, []);

    const showFields = duplicatesForm.enabled && !isUsers;

    const isConfirmDisabled = useMemo(() => {
        let fields = duplicatesForm.fields;
        const notAllowedType = duplicatesForm.type === 3;
        if (fields?.length) fields = fields.filter((el) => el !== null);
        return (showFields && !fields?.length) || notAllowedType;
    }, [duplicatesForm, showFields]);

    return {
        avoidDuplicatesProps: {
            modalConfirmIsOpen,
            setModalConfirmIsOpen,
            duplicatesForm,
            setDuplicatesForm,
            onCancelConfirm,
            modalLimitsIsOpen,
            limits,
            onConfirmLimits,
            onCancelLimits,
        },
        avoidDuplicatesFooterProps: {
            onConfirm,
            isConfirmDisabled: isConfirmDisabled,
        },
    };
};

export default useAvoidDuplicates;
